<template>
  <v-container
    fluid
    class="main-container responsive-height dashboard-page"
    :class="{ 'dashboard-page--light': !$vuetify.theme.dark }"
  >
    <div
      ref="tradingViewTicker"
      class="market-ticker tradingview-widget-container"
      aria-label="Live market ticker"
    >
    </div>

    <div class="dashboard-grid">
      <main class="dashboard-main">
        <div class="summary-row">
          <section class="dashboard-panel trading-summary">
            <header class="panel-header">
              <div class="panel-title">
                <span class="title-icon"><v-icon small>mdi-chart-timeline-variant</v-icon></span>
                Trading Summary
              </div>
              <v-menu offset-y bottom left content-class="dashboard-dropdown-menu">
                <template v-slot:activator="{ on, attrs }">
                  <button class="period-select" v-bind="attrs" v-on="on">
                    {{ periodLabel(selectedPeriods.summary) }}
                    <v-icon x-small>mdi-chevron-down</v-icon>
                  </button>
                </template>
                <v-list dense class="dashboard-dropdown-list">
                  <v-list-item
                    v-for="period in summaryPeriodOptions"
                    :key="period.value"
                    @click="selectedPeriods.summary = period.value"
                  >
                    <v-list-item-title>{{ period.label }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </header>

            <div class="summary-metrics">
              <article v-for="metric in summaryMetrics" :key="metric.label" class="summary-metric">
                <v-tooltip bottom open-delay="150">
                  <template v-slot:activator="{ on, attrs }">
                    <span class="metric-label metric-label--help" v-bind="attrs" v-on="on">
                      {{ metric.label }}
                      <v-icon x-small>mdi-information-outline</v-icon>
                    </span>
                  </template>
                  <span>{{ summaryCalculation(metric.key) }}</span>
                </v-tooltip>
                <strong>{{ metric.value }}</strong>
                <span class="metric-change" :class="metric.tone">{{ metric.change }}</span>
                <apexchart
                  class="sparkline"
                  type="area"
                  height="38"
                  :options="sparklineOptions(metric.color)"
                  :series="[{ name: metric.label, data: metric.series }]"
                />
              </article>
            </div>
          </section>

          <section class="dashboard-panel balance-overview">
            <header class="panel-header">
              <div class="panel-title">
                <span class="title-icon"><v-icon small>mdi-wallet-outline</v-icon></span>
                Balance Overview
              </div>
              <v-menu offset-y bottom left content-class="dashboard-dropdown-menu">
                <template v-slot:activator="{ on, attrs }">
                  <button class="period-select" v-bind="attrs" v-on="on">
                    {{ periodLabel(selectedPeriods.balance) }}
                    <v-icon x-small>mdi-chevron-down</v-icon>
                  </button>
                </template>
                <v-list dense class="dashboard-dropdown-list">
                  <v-list-item
                    v-for="period in balancePeriodOptions"
                    :key="period.value"
                    @click="selectedPeriods.balance = period.value"
                  >
                    <v-list-item-title>{{ period.label }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </header>
            <div class="balance-content">
              <div>
                <div class="balance-value">{{ money(balance) }} <small>USD</small></div>
                <p>Equity: {{ money(equity) }} USD</p>
                <p>Bonus: {{ money(balanceOverviewBonus) }} USD</p>
                <p>Total Profit: <strong :class="balanceOverviewProfit >= 0 ? 'positive' : 'negative'">{{ signedMoney(balanceOverviewProfit) }} USD</strong></p>
              </div>
              <router-link to="/deposit" class="mini-deposit">Deposit</router-link>
            </div>
            <apexchart
              class="balance-chart"
              type="area"
              height="70"
              :options="balanceChartOptions"
              :series="balanceChartSeries"
            />
          </section>
        </div>

        <nav class="quick-actions" aria-label="Dashboard quick actions">
          <router-link v-for="action in quickActions" :key="action.title" :to="action.link" class="quick-action">
            <span class="action-icon" :class="action.tone"><v-icon>{{ action.icon }}</v-icon></span>
            <span>{{ action.title }}</span>
          </router-link>
        </nav>

        <div class="accounts-performance-row">
          <section class="dashboard-panel trading-accounts">
            <header class="panel-header">
              <div class="panel-title">
                <span class="title-icon"><v-icon small>mdi-briefcase-outline</v-icon></span>
                Trading Accounts
              </div>
              <router-link to="/mt5-accounts" class="panel-link">View All <v-icon x-small>mdi-chevron-right</v-icon></router-link>
            </header>
            <div class="account-columns">
              <article class="account-stat">
                <span>Live Accounts</span>
                <strong>{{ liveAccountCount }}</strong>
                <b>{{ money(liveAccountBalance) }}</b>
                <small>Total Balance</small>
                <apexchart
                  class="account-sparkline"
                  type="area"
                  height="48"
                  :options="sparklineOptions('#5275ff')"
                  :series="liveAccountChartSeries"
                />
              </article>
              <article class="account-stat">
                <span>Social Accounts</span>
                <strong>{{ socialAccountCount }}</strong>
                <b>{{ money(socialAccountBalance) }}</b>
                <small>Total Balance</small>
                <apexchart
                  class="account-sparkline"
                  type="area"
                  height="48"
                  :options="sparklineOptions('#a23cff')"
                  :series="socialAccountChartSeries"
                />
              </article>
              <article class="account-stat">
                <span>Demo Accounts</span>
                <strong>{{ demoAccountCount }}</strong>
                <b>{{ money(demoBalance) }}</b>
                <small>Total Balance</small>
                <apexchart
                  class="account-sparkline"
                  type="area"
                  height="48"
                  :options="sparklineOptions('#38c66a')"
                  :series="demoAccountChartSeries"
                />
              </article>
            </div>
            <router-link to="/create-live-account" class="create-account-link">
              Create New Account <v-icon small>mdi-plus-circle</v-icon>
            </router-link>
          </section>

          <section class="dashboard-panel performance-overview">
            <header class="panel-header">
              <div class="panel-title">
                <span class="title-icon"><v-icon small>mdi-chart-box-outline</v-icon></span>
                Performance Overview
              </div>
              <v-menu offset-y bottom left content-class="dashboard-dropdown-menu">
                <template v-slot:activator="{ on, attrs }">
                  <button class="period-select" v-bind="attrs" v-on="on">
                    {{ periodLabel(selectedPeriods.performance) }}
                    <v-icon x-small>mdi-chevron-down</v-icon>
                  </button>
                </template>
                <v-list dense class="dashboard-dropdown-list">
                  <v-list-item
                    v-for="period in performancePeriodOptions"
                    :key="period.value"
                    @click="selectedPeriods.performance = period.value"
                  >
                    <v-list-item-title>{{ period.label }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </header>
            <div class="performance-chart-wrap">
              <apexchart
                type="line"
                height="185"
                :options="performanceChartOptions"
                :series="performanceChartSeries"
              />
            </div>
          </section>
        </div>

        <div class="transactions-trader-row">
          <section class="dashboard-panel recent-transactions">
            <header class="panel-header">
              <div class="panel-title">
                <span class="title-icon"><v-icon small>mdi-swap-horizontal-bold</v-icon></span>
                Recent Transactions
              </div>
              <router-link to="/transaction-history" class="panel-link">View All Transactions <v-icon x-small>mdi-chevron-right</v-icon></router-link>
            </header>
            <div class="transaction-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Date &amp; Time</th>
                    <th>Acc. No.</th>
                    <th>Amount ($)</th>
                    <th>Fee ($)</th>
                    <th>Net Amount ($)</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(transaction, index) in visibleTransactions" :key="transaction.id || index">
                    <td>
                      <span class="trx-icon" :class="transactionTone(transaction)">
                        <v-icon x-small>{{ transactionIcon(transaction) }}</v-icon>
                      </span>
                      {{ transactionLabel(transaction) }}
                    </td>
                    <td>{{ formatTransactionDate(transaction.updated_at || transaction.created_at) }}</td>
                    <td>{{ transaction.acc_no || transaction.from_acc_no || "N/A" }}</td>
                    <td>{{ money(transaction.amount) }}</td>
                    <td>{{ money(transaction.transaction_fee || transaction.trx_fee) }}</td>
                    <td>{{ money(transaction.net_amount !== undefined ? transaction.net_amount : transaction.amount) }}</td>
                    <td><span class="status-approved"><v-icon x-small>mdi-check</v-icon>{{ transaction.status || "Approved" }}</span></td>
                  </tr>
                  <tr v-if="!visibleTransactions.length">
                    <td colspan="7" class="empty-state">No recent transactions available.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section class="dashboard-panel trader-performance">
            <header class="panel-header">
              <div class="panel-title">
                <span class="title-icon"><v-icon small>mdi-trophy-outline</v-icon></span>
                Trader Performance
              </div>
              <v-menu offset-y bottom left content-class="dashboard-dropdown-menu">
                <template v-slot:activator="{ on, attrs }">
                  <button class="period-select" v-bind="attrs" v-on="on">
                    {{ periodLabel(selectedPeriods.trader) }}
                    <v-icon x-small>mdi-chevron-down</v-icon>
                  </button>
                </template>
                <v-list dense class="dashboard-dropdown-list">
                  <v-list-item
                    v-for="period in traderPeriodOptions"
                    :key="period.value"
                    @click="selectedPeriods.trader = period.value"
                  >
                    <v-list-item-title>{{ period.label }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </header>
            <div class="trader-performance-body">
              <div class="donut-chart-wrap">
                <apexchart
                  type="donut"
                  height="128"
                  :options="traderChartOptions"
                  :series="traderChartSeries"
                />
                <div class="donut-center">
                  <span>Total Profit</span>
                  <strong>{{ money(traderTotalProfit) }}</strong>
                </div>
              </div>
              <div class="performance-list">
                <p><i class="legend-green"></i>Profitable Days <strong>{{ profitableDays }} ({{ profitablePercent }}%)</strong></p>
                <p><i class="legend-red"></i>Losing Days <strong>{{ losingDays }} ({{ losingPercent }}%)</strong></p>
                <p><i class="legend-gray"></i>Break Even Days <strong>{{ breakEvenDays }} ({{ breakEvenPercent }}%)</strong></p>
              </div>
            </div>
            <div class="performance-footer">
              <div><span>Best Day</span><strong :class="bestDay >= 0 ? 'positive' : 'negative'">{{ money(bestDay) }}</strong><small>{{ bestDayDate }}</small></div>
              <div><span>Worst Day</span><strong :class="worstDay >= 0 ? 'positive' : 'negative'">{{ money(worstDay) }}</strong><small>{{ worstDayDate }}</small></div>
              <div><span>Avg Daily Profit</span><strong :class="avgDailyProfit >= 0 ? 'positive' : 'negative'">{{ money(avgDailyProfit) }}</strong><small>&nbsp;</small></div>
            </div>
          </section>
        </div>

        <section class="broker-banner">
          <div class="banner-copy">
            <h2>Trade with a Trusted Broker</h2>
            <p>Experience lightning-fast execution, tight spreads, and reliable support.</p>
            <router-link to="/create-live-account">Open Live Account</router-link>
          </div>
          <div class="banner-benefits">
            <div><span><v-icon>mdi-chart-timeline-variant-shimmer</v-icon></span><p><strong>Low Spreads</strong><small>From 0.0 pips</small></p></div>
            <div><span><v-icon>mdi-timer-outline</v-icon></span><p><strong>Fast Execution</strong><small>Under 20ms</small></p></div>
            <div><span><v-icon>mdi-headset</v-icon></span><p><strong>24/7 Support</strong><small>Always here</small></p></div>
          </div>
          <div class="banner-art" aria-hidden="true">
            <img src="@/assets/dashboard/trusted-broker-art.png" alt="">
          </div>
        </section>
      </main>

      <aside class="dashboard-aside">
        <section class="dashboard-panel economic-calendar">
          <header class="panel-header">
            <div class="panel-title">
              <span class="title-icon"><v-icon small>mdi-calendar-month-outline</v-icon></span>
              Economic Calendar
            </div>
            <router-link to="/economic-calendar" class="panel-link">View Calendar</router-link>
          </header>
          <div class="calendar-filters">
            <v-menu offset-y bottom left content-class="dashboard-dropdown-menu">
              <template v-slot:activator="{ on, attrs }">
                <button v-bind="attrs" v-on="on">
                  {{ calendarDay }}
                  <v-icon x-small>mdi-chevron-down</v-icon>
                </button>
              </template>
              <v-list dense class="dashboard-dropdown-list">
                <v-list-item v-for="day in calendarDayOptions" :key="day" @click="calendarDay = day">
                  <v-list-item-title>{{ day }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
            <v-menu offset-y bottom left content-class="dashboard-dropdown-menu">
              <template v-slot:activator="{ on, attrs }">
                <button v-bind="attrs" v-on="on">
                  {{ calendarImpact }}
                  <v-icon x-small>mdi-chevron-down</v-icon>
                </button>
              </template>
              <v-list dense class="dashboard-dropdown-list">
                <v-list-item v-for="impact in calendarImpactOptions" :key="impact" @click="calendarImpact = impact">
                  <v-list-item-title>{{ impact }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
          <div class="calendar-events">
            <div v-if="calendarLoading" class="calendar-empty">
              Loading economic events...
            </div>
            <article v-for="event in filteredEconomicEvents" :key="`${event.day}-${event.time}-${event.title}`">
              <time>{{ event.time }}</time>
              <span class="event-country">
                <span class="event-flag">
                  <img v-if="event.flagUrl" :src="event.flagUrl" :alt="`${event.country} flag`">
                  <i v-else>🌐</i>
                  <small>{{ event.country }}</small>
                </span>
                <b>{{ event.currency }}</b>
              </span>
              <div>
                <strong>{{ event.title }}</strong>
                <small>{{ event.details }}</small>
              </div>
              <em :class="event.impact.toLowerCase()">{{ event.impact }}</em>
            </article>
            <div v-if="!calendarLoading && !filteredEconomicEvents.length" class="calendar-empty">
              {{ calendarError || "No events match these filters." }}
            </div>
          </div>
        </section>

        <section class="dashboard-panel market-news">
          <header class="panel-header">
            <div class="panel-title">
              <span class="title-icon"><v-icon small>mdi-newspaper-variant-outline</v-icon></span>
              Market News
            </div>
            <router-link to="/news" class="panel-link">View All <v-icon x-small>mdi-chevron-right</v-icon></router-link>
          </header>
          <div class="news-list">
            <article v-for="(item, index) in visibleNews" :key="item.id || index">
              <div class="news-thumb" :class="`news-thumb-${index + 1}`"><v-icon>{{ newsIcon(index) }}</v-icon></div>
              <div>
                <time>{{ formatNewsDate(item.created_at, index) }}</time>
                <h4>{{ item.name || item.title }}</h4>
              </div>
              <span :class="newsTag(index).toLowerCase()">{{ newsTag(index) }}</span>
            </article>
          </div>
        </section>

        <section class="dashboard-panel dashboard-banners">
          <header class="panel-header">
            <div class="panel-title">
              <span class="title-icon"><v-icon small>mdi-image-multiple-outline</v-icon></span>
              Banners
            </div>
          </header>
          <div class="dashboard-banner-list">
            <transition name="dashboard-banner-fade" mode="out-in">
              <article
                v-if="currentDashboardBanner"
                :key="currentDashboardBanner.id || bannerIndex"
                class="dashboard-banner-slide"
              >
                <div class="dashboard-banner-copy">
                  <strong>{{ bannerCategory(currentDashboardBanner) }}</strong>
                </div>
                <div class="dashboard-banner-image">
                  <img
                    v-if="bannerImageAvailable(currentDashboardBanner)"
                    :src="bannerImage(currentDashboardBanner)"
                    :alt="bannerCategory(currentDashboardBanner)"
                    @error="markBrokenBannerImage(currentDashboardBanner)"
                  >
                  <div v-else class="dashboard-banner-placeholder">
                    <v-icon>mdi-image-off-outline</v-icon>
                  </div>
                </div>
              </article>
            </transition>
            <div v-if="!dashboard_banners.length" class="banner-empty">
              No banners available.
            </div>
          </div>
        </section>
      </aside>
    </div>
  </v-container>
</template>

<script>
import moment from "moment";
import axios from "axios";
import { mapActions, mapState } from "vuex";
import {
  NEWS__ACTIONS,
  PROFILE__ACTIONS,
  TRADING__ACTIONS,
  TRANSACTION__ACTIONS,
} from "@/store/action-types";

const ECONOMIC_CALENDAR_API = "https://apieconomic.finlogix.com/v1/getEconomicData";
const ECONOMIC_CALENDAR_COUNTRIES = [
  "US", "CH", "NZ", "CA", "CN", "FR", "AU", "JP", "EP", "GB", "DE", "IT",
];

export default {
  name: "ClientDashboard",
  components: {
    apexchart: () => import("vue-apexcharts"),
  },
  data() {
    return {
      periodOptions: [
        { label: "Today", value: "today" },
        { label: "This Week", value: "week" },
        { label: "This Month", value: "month" },
        { label: "Last 3 Months", value: "quarter" },
      ],
      summaryPeriodOptions: [
        { label: "Overall", value: "overall" },
        { label: "Today", value: "today" },
        { label: "This Week", value: "week" },
        { label: "This Month", value: "month" },
        { label: "Last 3 Months", value: "quarter" },
      ],
      performancePeriodOptions: [
        { label: "Overall", value: "overall" },
        { label: "Today", value: "today" },
        { label: "This Week", value: "week" },
        { label: "This Month", value: "month" },
        { label: "Last 3 Months", value: "quarter" },
      ],
      traderPeriodOptions: [
        { label: "Overall", value: "overall" },
        { label: "Today", value: "today" },
        { label: "This Week", value: "week" },
        { label: "This Month", value: "month" },
        { label: "Last 3 Months", value: "quarter" },
      ],
      balancePeriodOptions: [
        { label: "Current", value: "current" },
        { label: "Yesterday", value: "yesterday" },
        { label: "Last Month", value: "last_month" },
      ],
      selectedPeriods: {
        summary: "overall",
        balance: "current",
        performance: "overall",
        trader: "month",
      },
      calendarDay: "Today",
      calendarImpact: "All Impact",
      calendarDayOptions: ["Today", "Tomorrow", "This Week"],
      calendarImpactOptions: ["All Impact", "High", "Medium"],
      calendarLoading: false,
      calendarError: "",
      brokenBannerImages: {},
      bannerIndex: 0,
      bannerRotationTimer: null,
      quickActions: [
        { title: "Deposit", icon: "mdi-tray-arrow-down", link: "/deposit", tone: "blue" },
        { title: "Withdraw", icon: "mdi-bank-transfer-out", link: "/withdraw", tone: "purple" },
        { title: "Internal Transfer", icon: "mdi-swap-horizontal-bold", link: "/internal-transfer", tone: "green" },
        { title: "MT5 Accounts", icon: "mdi-chart-box-plus-outline", link: "/mt5-accounts", tone: "orange" },
        { title: "Verification", icon: "mdi-account-check-outline", link: "/profile-verify", tone: "cyan" },
      ],
      economicEvents: [],
      fallbackNews: [
        { id: "static-1", name: "Fed hints at rate cuts later this year as inflation cools" },
        { id: "static-2", name: "Gold prices surge on weaker dollar and rising demand" },
        { id: "static-3", name: "Tech stocks lead market rally ahead of earnings season" },
      ],
    };
  },
  computed: {
    ...mapState("account", [
      "account_summary",
      "performance_overview",
      "trading_summary",
      "trader_performance",
      "dashboard_banners",
    ]),
    ...mapState("company", ["generalInfo"]),
    ...mapState("news", ["news"]),
    ...mapState("profile", ["client"]),
    ...mapState("trading", [
      "totalLiveAccounts",
      "totalLiveAccountBalance",
      "totalSocialAccounts",
      "totalSocialAccountBalance",
      "demoAccounts",
    ]),
    ...mapState("transactions", ["recenttransactions"]),

    balance() {
      if (this.selectedPeriods.balance === "yesterday") {
        return Number(this.account_summary?.balancePrevDay) || 0;
      }
      if (this.selectedPeriods.balance === "last_month") {
        return Number(this.account_summary?.balancePrevMonth) || 0;
      }
      return Number(this.account_summary?.balance) || 0;
    },
    equity() {
      if (this.selectedPeriods.balance === "yesterday") {
        return Number(this.account_summary?.equityPrevDay) || 0;
      }
      if (this.selectedPeriods.balance === "last_month") {
        return Number(this.account_summary?.equityPrevMonth) || 0;
      }
      return Number(this.account_summary?.equity) || 0;
    },
    bonus() {
      return Number(this.account_summary?.bonus) || 0;
    },
    balanceOverviewBonus() {
      if (this.selectedPeriods.balance === "yesterday") {
        return Number(this.account_summary?.bonusPrevDay) || 0;
      }
      if (this.selectedPeriods.balance === "last_month") {
        return Number(this.account_summary?.bonusPrevMonth) || 0;
      }
      return this.bonus;
    },
    netProfit() {
      return Number(this.account_summary?.totalProfit) || 0;
    },
    balanceOverviewProfit() {
      if (this.selectedPeriods.balance === "yesterday") {
        return Number(this.account_summary?.totalProfitPrevDay) || 0;
      }
      if (this.selectedPeriods.balance === "last_month") {
        return Number(this.account_summary?.totalProfitPrevMonth) || 0;
      }
      return this.netProfit;
    },
    totalTrades() {
      return (Number(this.account_summary?.totalClosedTrades) || 0) + (Number(this.account_summary?.liveOrders) || 0);
    },
    winRate() {
      const value = Number(this.account_summary?.winRate || this.account_summary?.win_rate);
      return Number.isFinite(value) && value > 0 ? value : 62.5;
    },
    profitFactor() {
      const value = Number(this.account_summary?.profitFactor || this.account_summary?.profit_factor);
      return Number.isFinite(value) && value > 0 ? value : 1.85;
    },
    expectancy() {
      const closed = Number(this.account_summary?.totalClosedTrades) || 0;
      return closed > 0 ? this.netProfit / closed : 35.65;
    },
    summaryMetrics() {
      const metrics = this.trading_summary?.metrics || {};
      const changes = this.trading_summary?.changes || {};
      const series = this.trading_summary?.series || {};
      const changeDisplay = (key) => {
        if (this.selectedPeriods.summary === "overall") return "Overall";
        const value = Number(changes[key]) || 0;
        return `${value > 0 ? "+" : ""}${value.toFixed(2)}%`;
      };
      const changeTone = (key) => {
        const value = Number(changes[key]) || 0;
        return value > 0 ? "positive" : value < 0 ? "negative" : "neutral";
      };
      return [
        { key: "trades", label: "Total Trades", value: (Number(metrics.totalTrades) || 0).toLocaleString(), change: changeDisplay("totalTrades"), tone: changeTone("totalTrades"), color: "#3986ff", series: series.totalTrades || [] },
        { key: "rate", label: "Win Rate", value: `${(Number(metrics.winRate) || 0).toFixed(2)}%`, change: changeDisplay("winRate"), tone: changeTone("winRate"), color: "#39c66b", series: series.winRate || [] },
        { key: "profit", label: "Net Profit", value: `$${this.money(metrics.netProfit)}`, change: changeDisplay("netProfit"), tone: changeTone("netProfit"), color: "#3fcf70", series: series.netProfit || [] },
        { key: "factor", label: "Profit Factor", value: (Number(metrics.profitFactor) || 0).toFixed(2), change: changeDisplay("profitFactor"), tone: changeTone("profitFactor"), color: "#a43cff", series: series.profitFactor || [] },
        { key: "expectancy", label: "Expectancy", value: `$${this.money(metrics.expectancy)}`, change: changeDisplay("expectancy"), tone: changeTone("expectancy"), color: "#ff9400", series: series.expectancy || [] },
      ];
    },
    liveAccountCount() {
      return Number(this.totalLiveAccounts) || 0;
    },
    liveAccountBalance() {
      return Number(this.totalLiveAccountBalance) || 0;
    },
    socialAccountCount() {
      return Number(this.totalSocialAccounts) || 0;
    },
    socialAccountBalance() {
      return Number(this.totalSocialAccountBalance) || 0;
    },
    demoAccountCount() {
      return Array.isArray(this.demoAccounts) ? this.demoAccounts.length : 0;
    },
    demoBalance() {
      if (!Array.isArray(this.demoAccounts)) return 0;
      return this.demoAccounts.reduce(
        (total, account) => total + (Number(account.balance ?? account.Balance) || 0),
        0
      );
    },
    liveAccountChartSeries() {
      const current = this.liveAccountBalance || 12456.78;
      return [{
        name: "Live Balance",
        data: [.62, .69, .68, .75, .77, .88, .91, 1.08, .96, 1.03, 1]
          .map((ratio) => Number((current * ratio).toFixed(2))),
      }];
    },
    demoAccountChartSeries() {
      const current = this.demoBalance;
      return [{
        name: "Demo Balance",
        data: [.61, .64, .7, .67, .74, .76, .84, .88, 1.04, .94, .9]
          .map((ratio) => Number((current * ratio).toFixed(2))),
      }];
    },
    socialAccountChartSeries() {
      const current = this.socialAccountBalance || 12456.78;
      return [{
        name: "Social Balance",
        data: [.65, .72, .7, .78, .83, .8, .91, .96, 1.05, .98, 1]
          .map((ratio) => Number((current * ratio).toFixed(2))),
      }];
    },
    visibleTransactions() {
      const transactions = this.recenttransactions?.allTransactions;
      return Array.isArray(transactions) ? transactions.slice(0, 5) : [];
    },
    visibleNews() {
      return Array.isArray(this.news) && this.news.length ? this.news.slice(0, 3) : this.fallbackNews;
    },
    currentDashboardBanner() {
      if (!Array.isArray(this.dashboard_banners) || !this.dashboard_banners.length) return null;
      return this.dashboard_banners[this.bannerIndex % this.dashboard_banners.length];
    },
    filteredEconomicEvents() {
      const today = moment().startOf("day");
      const tomorrow = moment(today).add(1, "day");
      const weekEnd = moment(today).add(7, "days").endOf("day");
      return this.economicEvents.map((event) => {
        const date = moment(this.normalizedEconomicDate(event.date));
        const importance = Number(event.importance);
        const impact = importance >= 3 ? "High" : importance === 2 ? "Medium" : "Low";
        const forecastValue = event.forecast || event.teforecast;
        return {
          date,
          day: date.isSame(today, "day") ? "Today" : date.isSame(tomorrow, "day") ? "Tomorrow" : "This Week",
          time: date.isValid() ? date.format("hh:mm A") : "-",
          flagUrl: this.countryFlagUrl(event.country?.abbr),
          country: event.country?.abbr || "-",
          currency: event.country?.currency || event.country?.name || "-",
          title: event.event || "-",
          details: [
            event.actual ? `Actual: ${event.actual}` : "",
            forecastValue ? `Forecast: ${forecastValue}` : "",
          ].filter(Boolean).join("   "),
          impact,
        };
      }).filter((event) => {
        const withinWeek =
          event.date.isValid() &&
          event.date.isBetween(today, weekEnd, undefined, "[]");
        const dayMatches =
          this.calendarDay === "This Week"
            ? withinWeek
            : event.day === this.calendarDay;
        const impactMatches = this.calendarImpact === "All Impact" || event.impact === this.calendarImpact;
        return event.impact !== "Low" && dayMatches && impactMatches;
      }).sort((a, b) => a.date.valueOf() - b.date.valueOf()).slice(0, 4);
    },
    chartTextColor() {
      return this.$vuetify.theme.dark ? "#91a1b7" : "#6e7f93";
    },
    chartGridColor() {
      return this.$vuetify.theme.dark ? "#1d3044" : "#dce6f0";
    },
    balanceChartSeries() {
      return [
        {
          name: "Balance",
          data: [
            Number(this.account_summary?.balancePrevMonth) || 0,
            Number(this.account_summary?.balancePrevDay) || 0,
            Number(this.account_summary?.balance) || 0,
          ],
        },
      ];
    },
    balanceChartOptions() {
      const selectedPoint = {
        last_month: 0,
        yesterday: 1,
        current: 2,
      }[this.selectedPeriods.balance];
      return {
        chart: {
          type: "area",
          sparkline: { enabled: true },
          animations: { enabled: true, speed: 350 },
          toolbar: { show: false },
        },
        colors: ["#328cff"],
        stroke: { curve: "smooth", width: 2 },
        markers: {
          size: 3,
          strokeWidth: 2,
          strokeColors: "#ffffff",
          hover: { size: 6 },
          discrete: [{
            seriesIndex: 0,
            dataPointIndex: selectedPoint,
            fillColor: "#328cff",
            strokeColor: "#ffffff",
            size: 5,
          }],
        },
        fill: {
          type: "gradient",
          gradient: {
            shadeIntensity: 1,
            opacityFrom: .48,
            opacityTo: 0,
            stops: [0, 100],
          },
        },
        tooltip: {
          enabled: true,
          shared: false,
          intersect: false,
          followCursor: true,
          theme: this.$vuetify.theme.dark ? "dark" : "light",
          x: {
            show: true,
            formatter: (_, context) =>
              ["Last Month", "Yesterday", "Current"][context.dataPointIndex],
          },
          y: { formatter: (value) => `$${this.money(value)}` },
        },
      };
    },
    performanceChartSeries() {
      return [
        { name: "Profit", data: this.performance_overview?.profit || [] },
        { name: "Deposit", data: this.performance_overview?.deposit || [] },
        { name: "Withdraw", data: this.performance_overview?.withdraw || [] },
      ];
    },
    performanceChartOptions() {
      return {
        chart: {
          type: "line",
          background: "transparent",
          toolbar: { show: false },
          zoom: { enabled: false },
          animations: { enabled: true, speed: 350 },
          fontFamily: "inherit",
        },
        colors: ["#2e95ff", "#36bd69", "#ff4e4e"],
        stroke: { curve: "straight", width: [2, 2, 2] },
        markers: { size: 0, hover: { size: 4 } },
        dataLabels: { enabled: false },
        legend: {
          show: true,
          position: "top",
          horizontalAlign: "left",
          fontSize: "9px",
          labels: { colors: this.chartTextColor },
          markers: { width: 8, height: 8, radius: 0 },
          itemMargin: { horizontal: 10 },
        },
        grid: {
          borderColor: this.chartGridColor,
          strokeDashArray: 0,
          padding: { top: -8, right: 5, bottom: -5, left: 0 },
        },
        xaxis: {
          categories: this.performance_overview?.labels || [],
          axisBorder: { show: false },
          axisTicks: { show: false },
          labels: {
            style: { colors: this.chartTextColor, fontSize: "8px" },
            rotate: 0,
            hideOverlappingLabels: true,
          },
        },
        yaxis: {
          labels: {
            style: { colors: this.chartTextColor, fontSize: "8px" },
            formatter: (value) => {
              if (Math.abs(value) >= 1000) return `${(value / 1000).toFixed(0)}K`;
              return Math.round(value);
            },
          },
        },
        tooltip: {
          theme: this.$vuetify.theme.dark ? "dark" : "light",
          shared: true,
          y: { formatter: (value) => `$${this.money(value)}` },
        },
      };
    },
    tradingDays() {
      return Number(this.trader_performance?.tradingDays) || 0;
    },
    profitableDays() {
      return Number(this.trader_performance?.profitableDays) || 0;
    },
    losingDays() {
      return Number(this.trader_performance?.losingDays) || 0;
    },
    breakEvenDays() {
      return Number(this.trader_performance?.breakEvenDays) || 0;
    },
    profitablePercent() {
      return Number(this.trader_performance?.profitablePercent) || 0;
    },
    losingPercent() {
      return Number(this.trader_performance?.losingPercent) || 0;
    },
    breakEvenPercent() {
      return Number(this.trader_performance?.breakEvenPercent) || 0;
    },
    traderTotalProfit() {
      return Number(this.trader_performance?.totalProfit) || 0;
    },
    showTraderDistribution() {
      return this.tradingDays > 0 && this.traderTotalProfit !== 0;
    },
    traderChartSeries() {
      return this.showTraderDistribution
        ? [this.profitableDays, this.losingDays, this.breakEvenDays]
        : [1];
    },
    traderChartOptions() {
      return {
        chart: {
          type: "donut",
          sparkline: { enabled: true },
          animations: { enabled: true, speed: 350 },
        },
        labels: this.showTraderDistribution
          ? ["Profitable Days", "Losing Days", "Break Even Days"]
          : ["No Profit Data"],
        colors: this.showTraderDistribution
          ? ["#3ec76d", "#ff514f", "#6f829b"]
          : ["#33465c"],
        stroke: { width: 0 },
        dataLabels: { enabled: false },
        legend: { show: false },
        plotOptions: {
          pie: {
            donut: { size: "67%" },
            expandOnClick: false,
          },
        },
        tooltip: {
          theme: this.$vuetify.theme.dark ? "dark" : "light",
          y: {
            formatter: (value) =>
              this.showTraderDistribution
                ? `${value} day${value === 1 ? "" : "s"}`
                : "No trades or profit",
          },
        },
      };
    },
    bestDay() {
      return Number(this.trader_performance?.bestDay) || 0;
    },
    worstDay() {
      return Number(this.trader_performance?.worstDay) || 0;
    },
    avgDailyProfit() {
      return Number(this.trader_performance?.avgDailyProfit) || 0;
    },
    bestDayDate() {
      return this.trader_performance?.bestDayDate || "N/A";
    },
    worstDayDate() {
      return this.trader_performance?.worstDayDate || "N/A";
    },
  },
  watch: {
    dashboard_banners: {
      handler() {
        this.bannerIndex = 0;
        this.startBannerRotation();
      },
      immediate: true,
    },
    "$vuetify.theme.dark"() {
      this.$nextTick(this.renderTradingViewTicker);
    },
    "selectedPeriods.performance"(period) {
      this[PROFILE__ACTIONS.DASHBOARDPERFORMANCE](period);
    },
    "selectedPeriods.summary"(period) {
      this[PROFILE__ACTIONS.DASHBOARDTRADINGSUMMARY](period);
    },
    "selectedPeriods.trader"(period) {
      this[PROFILE__ACTIONS.DASHBOARDTRADERPERFORMANCE](period);
    },
  },
  methods: {
    ...mapActions("account", [
      PROFILE__ACTIONS.ACCOUNTSUMMARY,
      PROFILE__ACTIONS.DASHBOARDPERFORMANCE,
      PROFILE__ACTIONS.DASHBOARDTRADINGSUMMARY,
      PROFILE__ACTIONS.DASHBOARDTRADERPERFORMANCE,
      PROFILE__ACTIONS.DASHBOARDBANNERS,
    ]),
    ...mapActions("news", [NEWS__ACTIONS.GETNEWS]),
    ...mapActions("trading", [TRADING__ACTIONS.TOTALLIVEACCOUNTS, TRADING__ACTIONS.DEMOACCOUNTS]),
    ...mapActions("transactions", [TRANSACTION__ACTIONS.RECENTTRANSACTIONHISTORY]),

    clearTradingViewTicker() {
      const container = this.$refs.tradingViewTicker;

      if (!container) return;

      while (container.firstChild) {
        container.removeChild(container.firstChild);
      }
    },
    renderTradingViewTicker() {
      const container = this.$refs.tradingViewTicker;

      if (!container) return;

      this.clearTradingViewTicker();

      const widget = document.createElement("div");
      widget.className = "tradingview-widget-container__widget";
      container.appendChild(widget);

      const script = document.createElement("script");
      script.type = "text/javascript";
      script.async = true;
      script.src =
        "https://s3.tradingview.com/external-embedding/embed-widget-ticker-tape.js";
      script.innerHTML = JSON.stringify({
        symbols: [
          { description: "EUR/USD", proName: "FX:EURUSD" },
          { description: "GBP/USD", proName: "FX:GBPUSD" },
          { description: "USD/JPY", proName: "FX:USDJPY" },
          { description: "USD/CAD", proName: "FX:USDCAD" },
          { description: "AUD/USD", proName: "FX:AUDUSD" },
          { description: "Gold", proName: "OANDA:XAUUSD" },
          { description: "Silver", proName: "OANDA:XAGUSD" },
          { description: "US Oil", proName: "TVC:USOIL" },
          { description: "Bitcoin", proName: "BINANCE:BTCUSDT" },
          { description: "Ethereum", proName: "BINANCE:ETHUSDT" },
          { description: "BNB", proName: "BINANCE:BNBUSDT" },
          { description: "S&P 500", proName: "FOREXCOM:SPXUSD" },
          { description: "Nasdaq 100", proName: "FOREXCOM:NSXUSD" },
          { description: "Dow Jones", proName: "FOREXCOM:DJI" },
          { description: "DAX", proName: "XETR:DAX" },
        ],
        colorTheme: this.$vuetify.theme.dark ? "dark" : "light",
        isTransparent: true,
        displayMode: "adaptive",
        locale: "en",
      });

      container.appendChild(script);
    },
    sparklineOptions(color) {
      return {
        chart: {
          type: "area",
          sparkline: { enabled: true },
          animations: { enabled: true, speed: 300 },
          toolbar: { show: false },
        },
        colors: [color],
        stroke: { curve: "smooth", width: 1.5 },
        fill: {
          type: "gradient",
          gradient: {
            opacityFrom: .4,
            opacityTo: 0,
            stops: [0, 100],
          },
        },
        tooltip: {
          theme: this.$vuetify.theme.dark ? "dark" : "light",
          x: { show: false },
        },
      };
    },
    summaryCalculation(key) {
      const metrics = this.trading_summary?.metrics || {};
      const total = Number(metrics.totalTrades) || 0;
      const winning = Number(metrics.winningTrades) || 0;
      const losing = Number(metrics.losingTrades) || 0;
      const breakEven = Number(metrics.breakEvenTrades) || 0;
      const grossProfit = Number(metrics.grossProfit) || 0;
      const grossLoss = Number(metrics.grossLoss) || 0;
      const netProfit = Number(metrics.netProfit) || 0;
      const calculations = {
        trades: `Total Trades = Winning + Losing + Break Even = ${winning} + ${losing} + ${breakEven} = ${total}`,
        rate: `Win Rate = Winning Trades ÷ Total Trades × 100 = ${winning} ÷ ${total} × 100 = ${(Number(metrics.winRate) || 0).toFixed(2)}%`,
        profit: `Net Profit = Gross Profit − Gross Loss = $${this.money(grossProfit)} − $${this.money(grossLoss)} = $${this.money(netProfit)}`,
        factor: `Profit Factor = Gross Profit ÷ Gross Loss = $${this.money(grossProfit)} ÷ $${this.money(grossLoss)} = ${(Number(metrics.profitFactor) || 0).toFixed(2)}`,
        expectancy: `Expectancy = Net Profit ÷ Total Trades = $${this.money(netProfit)} ÷ ${total} = $${this.money(metrics.expectancy)}`,
      };
      return calculations[key] || "";
    },
    normalizedEconomicDate(date) {
      if (!date) return "";
      return date.includes("T") ? date : date.replace(" ", "T");
    },
    countryFlagUrl(countryCode) {
      const code = String(countryCode || "").toUpperCase();
      const flagCode = code === "EP" ? "EU" : code;
      return /^[A-Z]{2}$/.test(flagCode)
        ? `https://flagcdn.com/16x12/${flagCode.toLowerCase()}.png`
        : "";
    },
    async fetchDashboardEconomicCalendar() {
      this.calendarLoading = true;
      this.calendarError = "";
      const start = moment().startOf("day");
      const end = moment(start).add(7, "days");
      try {
        const { data } = await axios.get(ECONOMIC_CALENDAR_API, {
          params: {
            start: `${start.format("YYYY-MM-DD")}T00:00:00Z`,
            end: `${end.format("YYYY-MM-DD")}T23:59:59Z`,
            countries: ECONOMIC_CALENDAR_COUNTRIES.join(","),
          },
        });
        this.economicEvents = Array.isArray(data?.data) ? data.data : [];
      } catch (error) {
        console.error(error);
        this.economicEvents = [];
        this.calendarError = "Unable to load economic events.";
      } finally {
        this.calendarLoading = false;
      }
    },
    periodLabel(value) {
      const options = [
        ...this.balancePeriodOptions,
        ...this.summaryPeriodOptions,
        ...this.performancePeriodOptions,
        ...this.traderPeriodOptions,
        ...this.periodOptions,
      ];
      return options.find((period) => period.value === value)?.label || "This Month";
    },
    chartPointCount(period) {
      return { today: 6, week: 8, month: 18, quarter: 24, overall: 24 }[period] || 18;
    },
    resampleSeries(values, count) {
      const source = Array.isArray(values) && values.length ? values : [0];
      if (count === 1) return [source[source.length - 1]];
      return Array.from({ length: count }, (_, index) => {
        const sourceIndex = Math.round((index / (count - 1)) * (source.length - 1));
        return Number(Number(source[sourceIndex] || 0).toFixed(2));
      });
    },
    buildChartCategories(period, count) {
      if (period === "today") {
        return Array.from({ length: count }, (_, index) =>
          moment().startOf("day").add(index * 4, "hours").format("HH:mm")
        );
      }
      if (period === "week") {
        return Array.from({ length: count }, (_, index) =>
          moment().subtract(count - index - 1, "days").format("ddd")
        );
      }
      if (period === "quarter") {
        return Array.from({ length: count }, (_, index) =>
          moment().subtract((count - index - 1) * 4, "days").format("DD MMM")
        );
      }
      return Array.from({ length: count }, (_, index) =>
        moment().subtract((count - index - 1) * 2, "days").format("DD MMM")
      );
    },
    money(value) {
      const number = Number(value);
      return (Number.isFinite(number) ? number : 0).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    signedMoney(value) {
      const number = Number(value) || 0;
      return `${number >= 0 ? "+" : "-"}${this.money(Math.abs(number))}`;
    },
    transactionLabel(transaction) {
      return transaction.trx_type || transaction.type || "Internal Transfer";
    },
    transactionTone(transaction) {
      const label = this.transactionLabel(transaction).toLowerCase();
      if (label.includes("withdraw")) return "withdraw";
      if (label.includes("transfer")) return "transfer";
      return "deposit";
    },
    transactionIcon(transaction) {
      const tone = this.transactionTone(transaction);
      if (tone === "withdraw") return "mdi-arrow-up";
      if (tone === "transfer") return "mdi-swap-horizontal";
      return "mdi-arrow-down";
    },
    formatTransactionDate(date) {
      return date ? moment(date).format("DD.MM.YYYY - hh:mm a") : "N/A";
    },
    formatNewsDate(date, index) {
      return date ? moment(date).format("hh:mm A") : ["10:30 AM", "09:15 AM", "08:45 AM"][index];
    },
    newsIcon(index) {
      return ["mdi-bank-outline", "mdi-gold", "mdi-finance"][index] || "mdi-newspaper-variant-outline";
    },
    newsTag(index) {
      return ["Forex", "Commodities", "Stocks"][index] || "Market";
    },
    bannerImage(banner) {
      const image =
        banner?.banner_file ||
        banner?.image ||
        banner?.banner_image ||
        banner?.banner ||
        banner?.banner_url ||
        banner?.banner_path ||
        banner?.image_url ||
        banner?.file ||
        banner?.path;
      if (!image) return "";
      if (/^(https?:|data:)/i.test(image)) return image;
      const apiBase =
        process.env.VUE_APP_BASE_URL ||
        process.env.VUE_APP_LOCAL_BASE_URL ||
        "";
      const assetBase = apiBase.replace(/\/api\/v\d+\/?$/i, "").replace(/\/$/, "");
      return `${assetBase}/${String(image).replace(/^\//, "")}`;
    },
    bannerImageAvailable(banner) {
      return Boolean(this.bannerImage(banner)) && !this.brokenBannerImages[banner?.id];
    },
    bannerCategory(banner) {
      const category = banner?.banner_category || banner?.bannerCategory;
      return category?.name || category?.category_name || banner?.category || "Promotion";
    },
    markBrokenBannerImage(banner) {
      this.$set(this.brokenBannerImages, banner?.id, true);
    },
    startBannerRotation() {
      this.stopBannerRotation();
      if (!Array.isArray(this.dashboard_banners) || this.dashboard_banners.length < 2) return;

      this.bannerRotationTimer = window.setInterval(() => {
        this.bannerIndex = (this.bannerIndex + 1) % this.dashboard_banners.length;
      }, 5000);
    },
    stopBannerRotation() {
      if (!this.bannerRotationTimer) return;
      window.clearInterval(this.bannerRotationTimer);
      this.bannerRotationTimer = null;
    },
  },
  async mounted() {
    // The ticker is independent of dashboard API data, so initialize it as soon
    // as its container exists instead of waiting for every request to finish.
    this.$nextTick(this.renderTradingViewTicker);

    const requests = [
      this[PROFILE__ACTIONS.ACCOUNTSUMMARY]({ trackProgress: false }),
      this[PROFILE__ACTIONS.DASHBOARDTRADINGSUMMARY](this.selectedPeriods.summary),
      this[PROFILE__ACTIONS.DASHBOARDTRADERPERFORMANCE](this.selectedPeriods.trader),
      this[PROFILE__ACTIONS.DASHBOARDBANNERS](),
      this[PROFILE__ACTIONS.DASHBOARDPERFORMANCE](this.selectedPeriods.performance),
      this[TRADING__ACTIONS.TOTALLIVEACCOUNTS](),
      this[TRADING__ACTIONS.DEMOACCOUNTS](),
      this[TRANSACTION__ACTIONS.RECENTTRANSACTIONHISTORY]({ trackProgress: false }),
      this[NEWS__ACTIONS.GETNEWS]({ page: 1, limit: 3 }),
      this.fetchDashboardEconomicCalendar(),
    ];
    await Promise.allSettled(requests);
    document.title = `Dashboard | ${this.generalInfo?.company_name || "Client Panel"}`;
  },
  beforeDestroy() {
    this.clearTradingViewTicker();
    this.stopBannerRotation();
  },
};
</script>

<style scoped>
.dashboard-page {
  --page: #050b13;
  --panel: #091522;
  --panel-soft: #0d1927;
  --border: #1a2a3b;
  --text: #f2f6fc;
  --muted: #91a1b7;
  --blue: #f4c515;
  --green: #39c66b;
  --red: #ff5252;
  --purple: #a23cff;
  min-height: calc(100vh - 65px);
  padding: 12px 8px 26px !important;
  background:
    radial-gradient(circle at 62% 0%, rgba(130, 94, 0, .13), transparent 28%),
    var(--page);
  color: var(--text);
  font-size: 12px;
}

.dashboard-page--light {
  --page: #eef4f9;
  --panel: #ffffff;
  --panel-soft: #f6f9fc;
  --border: #dce6f0;
  --text: #142033;
  --muted: #6e7f93;
}

.market-ticker {
  width: 100%;
  min-height: 46px;
  margin-bottom: 10px;
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 7px;
  background: linear-gradient(90deg, #0b1724, rgba(7, 13, 21, .88));
}

.dashboard-page--light .market-ticker {
  background: #ffffff;
  box-shadow: 0 5px 18px rgba(23, 51, 79, .05);
}

.positive { color: var(--green) !important; }
.negative { color: var(--red) !important; }
.neutral { color: var(--muted) !important; }
.blue-text { color: #3891ff !important; }
.purple-text { color: #ba53ff !important; }
.orange-text { color: #ff9800 !important; }

.dashboard-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 316px;
  gap: 10px;
}

.dashboard-main { min-width: 0; }
.dashboard-panel {
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 7px;
  background:
    radial-gradient(circle at 86% 0%, rgba(121, 88, 0, .12), transparent 35%),
    var(--panel);
  box-shadow: 0 10px 25px rgba(0, 0, 0, .06);
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 43px;
  padding: 0 12px;
  border-bottom: 1px solid var(--border);
}

.panel-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--text);
  font-size: 12px;
  font-weight: 600;
}

.title-icon,
.title-icon .v-icon { color: var(--blue) !important; }
.title-icon { display: inline-flex; }

.period-select,
.calendar-filters button {
  height: 27px;
  padding: 0 9px;
  border: 1px solid var(--border);
  border-radius: 4px;
  background: rgba(255, 255, 255, .015);
  color: var(--muted);
  font-size: 9px;
}

.period-select .v-icon,
.calendar-filters .v-icon { color: var(--muted); }

.panel-link {
  color: #f4c515 !important;
  text-decoration: none;
  font-size: 10px;
}
.panel-link .v-icon { color: #f4c515; }

.summary-row {
  display: grid;
  grid-template-columns: minmax(0, 2.25fr) minmax(250px, 1fr);
  gap: 8px;
}

.summary-metrics {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  height: 136px;
}

.summary-metric {
  position: relative;
  min-width: 0;
  padding: 13px 14px 4px;
  overflow: hidden;
  border-right: 1px solid var(--border);
}

.summary-metric:last-child { border-right: 0; }
.metric-label { display: block; color: var(--muted); font-size: 9px; }
.metric-label--help {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  cursor: help;
}
.metric-label--help .v-icon {
  color: var(--muted);
  font-size: 12px !important;
}
.summary-metric strong {
  display: block;
  margin-top: 5px;
  color: var(--text);
  font-size: 16px;
  line-height: 1.15;
  white-space: nowrap;
}

.metric-change {
  display: block;
  margin-top: 7px;
  font-size: 9px;
}

.sparkline {
  position: absolute;
  right: 12px;
  bottom: 8px;
  left: 12px;
  width: calc(100% - 24px);
  height: 38px;
}

.balance-overview { position: relative; min-height: 181px; }
.balance-content {
  position: static;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  padding: 11px 12px;
}

.balance-content > div { position: relative; z-index: 1; }
.balance-value { margin-bottom: 8px; color: var(--text); font-size: 20px; line-height: 1; }
.balance-value small { font-size: 11px; font-weight: 400; }
.balance-content p { margin: 0 0 3px; color: var(--muted); font-size: 9px; }
.balance-content p strong { font-weight: 500; }
.mini-deposit {
  position: absolute;
  top: 53px;
  right: 12px;
  bottom: auto;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 27px;
  padding: 0 16px;
  border-radius: 3px;
  background: linear-gradient(135deg, #ffdc42, #d99f00);
  color: #121820 !important;
  font-weight: 700;
  text-decoration: none;
  font-size: 10px;
}
.balance-chart { position: absolute; right: 0; bottom: 0; left: 0; width: 100%; height: 64px; }

.quick-actions {
  display: grid;
  grid-template-columns: repeat(5, minmax(95px, 1fr));
  gap: 8px;
  margin: 8px 0;
}

.quick-action {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 79px;
  border: 1px solid var(--border);
  border-radius: 7px;
  background: var(--panel);
  color: var(--text) !important;
  text-decoration: none;
  font-size: 10px;
  transition: transform .18s ease, border-color .18s ease;
}
.quick-action:hover { transform: translateY(-2px); border-color: #f4c515; }
.action-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 35px;
  height: 35px;
  margin-bottom: 6px;
  border: 1px solid rgba(255, 255, 255, .2);
  border-radius: 50%;
  box-shadow: 0 5px 14px rgba(0, 0, 0, .25);
}
.action-icon .v-icon {
  color: #ffffff !important;
  font-size: 21px;
  opacity: 1 !important;
  text-shadow: 0 1px 4px rgba(0, 0, 0, .35);
}
.action-icon.blue, .action-icon.purple, .action-icon.green, .action-icon.orange, .action-icon.cyan { background: linear-gradient(145deg, #6d5815, #2c250d); border-color: rgba(244, 197, 21, .28); }

.accounts-performance-row,
.transactions-trader-row {
  display: grid;
  gap: 8px;
  margin-bottom: 8px;
}
.accounts-performance-row { grid-template-columns: minmax(330px, .86fr) minmax(0, 1.14fr); }
.transactions-trader-row { grid-template-columns: minmax(0, 1.46fr) minmax(330px, 1fr); }

.trading-accounts,
.performance-overview { height: 238px; }

.account-columns { display: grid; grid-template-columns: repeat(3, 1fr); }
.account-stat {
  position: relative;
  height: 159px;
  padding: 16px 15px;
  overflow: hidden;
  border-right: 1px solid var(--border);
}
.account-stat:last-child { border-right: 0; }
.account-stat > span { display: block; color: var(--muted); font-size: 10px; }
.account-stat > strong { display: block; margin: 5px 0 17px; color: var(--text); font-size: 18px; }
.account-stat > b { display: block; color: var(--text); font-size: 14px; }
.account-stat > small { color: var(--muted); font-size: 9px; }
.account-sparkline { position: absolute; right: 12px; bottom: 6px; left: 12px; width: calc(100% - 24px); height: 48px; }
.create-account-link {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 35px;
  border-top: 1px solid var(--border);
  color: #f4c515 !important;
  text-decoration: none;
  font-size: 10px;
}
.create-account-link .v-icon { margin-left: 6px; color: #f4c515; }

.performance-list p { display: flex; align-items: center; gap: 7px; }
.performance-list i { width: 8px; height: 8px; }
.legend-blue { background: #2e95ff; }
.legend-green { background: #3ec76d; }
.legend-red { background: #ff514f; }
.legend-gray { background: #6f829b; }
.performance-chart-wrap { position: relative; height: 194px; padding: 0 5px; }

.recent-transactions,
.trader-performance { height: 255px; }
.transaction-table-wrap { overflow-x: auto; }
table { width: 100%; border-collapse: collapse; white-space: nowrap; }
th {
  height: 29px;
  padding: 0 10px;
  color: var(--muted);
  text-align: left;
  font-size: 8px;
  font-weight: 400;
}
td {
  height: 34px;
  padding: 0 10px;
  border-top: 1px solid rgba(72, 94, 119, .22);
  color: var(--text);
  font-size: 8px;
}
.trx-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  margin-right: 6px;
  border-radius: 4px;
}
.trx-icon.deposit { background: rgba(49, 194, 100, .16); }
.trx-icon.deposit .v-icon { color: #35c36a; }
.trx-icon.withdraw { background: rgba(255, 78, 78, .16); }
.trx-icon.withdraw .v-icon { color: #ff5555; }
.trx-icon.transfer { background: rgba(244, 174, 18, .17); }
.trx-icon.transfer .v-icon { color: #f3ae1d; }
.status-approved { color: #35c36a; }
.status-approved .v-icon { margin-right: 3px; color: #35c36a; }
.empty-state { height: 130px; color: var(--muted); text-align: center; }

.trader-performance-body {
  display: grid;
  grid-template-columns: 145px 1fr;
  align-items: center;
  min-height: 142px;
  padding: 8px 13px 5px;
}
.donut-chart-wrap {
  position: relative;
  width: 128px;
  height: 128px;
  margin: auto;
}
.donut-center {
  position: absolute;
  z-index: 2;
  top: 50%;
  left: 50%;
  width: 78px;
  text-align: center;
  transform: translate(-50%, -50%);
  pointer-events: none;
}
.donut-center span { display: block; color: var(--muted); font-size: 9px; }
.donut-center strong { display: block; color: var(--text); font-size: 15px; }
.performance-list p { justify-content: space-between; margin: 0 0 12px; color: var(--text); font-size: 8px; }
.performance-list p i { flex: 0 0 8px; }
.performance-list p strong { margin-left: auto; font-weight: 500; }
.performance-footer {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  height: 68px;
  border-top: 1px solid var(--border);
}
.performance-footer > div { display: flex; flex-direction: column; justify-content: center; padding-left: 15px; border-right: 1px solid var(--border); }
.performance-footer > div:last-child { border-right: 0; }
.performance-footer span { color: var(--muted); font-size: 9px; }
.performance-footer strong { margin: 4px 0 2px; font-size: 13px; }
.performance-footer small { color: var(--muted); font-size: 8px; }

.broker-banner {
  position: relative;
  display: grid;
  grid-template-columns: 1.25fr 1.6fr 1fr;
  align-items: center;
  min-height: 106px;
  padding: 15px 28px;
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 7px;
  background:
    linear-gradient(90deg, rgba(31, 30, 22, .84), rgba(9, 17, 24, .88)),
    var(--panel);
}

.dashboard-page--light .broker-banner {
  border-color: #d5e2ee;
  background:
    linear-gradient(90deg, rgba(255, 255, 255, .98), rgba(237, 245, 253, .94)),
    #ffffff;
  box-shadow: 0 8px 24px rgba(32, 61, 90, .08);
}

.dashboard-page--light .banner-copy h2 {
  color: #142033;
}

.dashboard-page--light .banner-copy p {
  color: #65778d;
}

.dashboard-page--light .banner-benefits p {
  color: #142033;
}

.dashboard-page--light .banner-benefits small {
  color: #65778d;
}

.dashboard-page--light .banner-benefits > div > span {
  border: 1px solid #d7e3ef;
  background: #eef4fb;
}

.banner-copy { position: relative; z-index: 2; }
.banner-copy h2 { margin: 0; color: #ffffff; font-size: 18px; line-height: 1.2; }
.banner-copy p { margin: 3px 0 9px; color: #b8c6d7; font-size: 9px; }
.banner-copy a {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 153px;
  height: 30px;
  border-radius: 3px;
  background: linear-gradient(90deg, #ffda40, #dba300);
  color: #101722 !important;
  font-weight: 700;
  text-decoration: none;
  font-size: 10px;
}
.banner-benefits {
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}
.banner-benefits > div { display: flex; align-items: center; gap: 9px; }
.banner-benefits > div > span {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 39px;
  width: 39px;
  height: 39px;
  border-radius: 50%;
  background: rgba(101, 78, 13, .36);
}
.banner-benefits .v-icon { color: #f4c515; }
.banner-benefits p { display: flex; flex-direction: column; margin: 0; color: #f3f7fc; font-size: 9px; }
.banner-benefits small { margin-top: 3px; color: #aebed0; font-size: 8px; }
.banner-art {
  position: absolute;
  right: 0;
  bottom: 0;
  width: min(43%, 530px);
  height: 100%;
  pointer-events: none;
}
.banner-art img {
  position: absolute;
  right: -4px;
  bottom: -2px;
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: right bottom;
}

.dashboard-aside { display: flex; flex-direction: column; gap: 10px; }
.economic-calendar { min-height: 0; }
.calendar-filters { display: flex; gap: 8px; padding: 10px 12px 5px; }
.calendar-filters button { min-width: 76px; text-align: left; }
.calendar-events { padding: 0 12px; }
.calendar-events article {
  display: grid;
  grid-template-columns: 42px 55px minmax(0, 1fr) auto;
  align-items: start;
  gap: 5px;
  min-height: 54px;
  padding: 10px 0;
  border-bottom: 1px solid rgba(72, 94, 119, .25);
}
.calendar-events time { color: var(--muted); font-size: 8px; }
.event-country { display: flex; align-items: center; gap: 6px; color: var(--text); font-size: 8px; }
.event-country b { font-weight: 500; }
.event-flag {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  min-width: 18px;
}
.event-flag i { font-size: 14px; font-style: normal; line-height: 1; }
.event-flag img {
  display: block;
  width: 18px;
  height: 18px;
  object-fit: cover;
  border: 1px solid rgba(255, 255, 255, .22);
  border-radius: 50%;
  box-shadow: 0 1px 4px rgba(0, 0, 0, .28);
}
.event-flag small { margin-top: 2px; color: var(--muted); font-size: 6px; line-height: 1; }
.calendar-events article > div { min-width: 0; }
.calendar-events strong { display: block; overflow: hidden; color: var(--text); font-size: 8px; font-weight: 500; text-overflow: ellipsis; white-space: nowrap; }
.calendar-events small { display: block; margin-top: 5px; color: var(--muted); font-size: 7px; white-space: pre; }
.calendar-events em { padding: 3px 5px; border-radius: 3px; font-size: 7px; font-style: normal; }
.calendar-events em.high { border: 1px solid rgba(255, 77, 64, .4); background: rgba(255, 77, 64, .14); color: #ff655b; }
.calendar-events em.medium { border: 1px solid rgba(244, 146, 24, .4); background: rgba(244, 146, 24, .14); color: #ff9f29; }
.calendar-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 210px;
  color: var(--muted);
  font-size: 9px;
}
.calendar-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 35px;
  color: #4f8bff !important;
  text-decoration: none;
  font-size: 9px;
}

.market-news { min-height: 299px; }
.news-list { padding: 0 12px; }
.news-list article {
  display: grid;
  grid-template-columns: 58px minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  min-height: 79px;
  border-bottom: 1px solid rgba(72, 94, 119, .25);
}
.news-list article:last-child { border-bottom: 0; }
.news-thumb {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 58px;
  height: 56px;
  border: 1px solid var(--border);
  border-radius: 3px;
  background: linear-gradient(135deg, #263d58, #0c1624);
}
.news-thumb-2 { background: linear-gradient(135deg, #9d6813, #2c1b05); }
.news-thumb-3 { background: linear-gradient(135deg, #7b1e24, #142a49); }
.news-thumb .v-icon { color: rgba(255,255,255,.8); }
.news-list time { color: var(--muted); font-size: 8px; }
.news-list h4 {
  display: -webkit-box;
  margin: 5px 0 0;
  overflow: hidden;
  color: var(--text);
  font-size: 9px;
  font-weight: 500;
  line-height: 1.45;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}
.news-list article > span { padding: 3px 6px; border-radius: 3px; font-size: 7px; }
.news-list article > span.forex { background: rgba(44, 100, 214, .3); color: #6e9cff; }
.news-list article > span.commodities { background: rgba(144, 52, 175, .3); color: #cf75ee; }
.news-list article > span.stocks { background: rgba(165, 78, 42, .3); color: #ec9670; }

.dashboard-banners {
  display: flex;
  flex: 1;
  min-height: 0;
  flex-direction: column;
}
.dashboard-banner-list {
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 12px;
}
.dashboard-banner-list article {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 11px;
  min-height: 150px;
  padding: 4px 0 12px;
}
.dashboard-banner-image {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: auto;
  min-height: 0;
  flex: 0 0 auto;
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: linear-gradient(135deg, #263d58, #0c1624);
}
.dashboard-banner-fade-enter-active,
.dashboard-banner-fade-leave-active { transition: opacity .35s ease, transform .35s ease; }
.dashboard-banner-fade-enter { opacity: 0; transform: translateX(8px); }
.dashboard-banner-fade-leave-to { opacity: 0; transform: translateX(-8px); }
.dashboard-banner-image img {
  display: block;
  width: 100%;
  height: auto;
}
.dashboard-banner-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background:
    radial-gradient(circle at 30% 25%, rgba(76, 111, 255, .28), transparent 42%),
    linear-gradient(135deg, #263d58, #0c1624);
}
.dashboard-banner-image .v-icon { color: var(--muted); }
.dashboard-banner-copy { min-width: 0; }
.dashboard-banner-copy strong {
  display: -webkit-box;
  overflow: hidden;
  color: var(--text);
  font-size: 12px;
  font-weight: 700;
  line-height: 1.45;
  letter-spacing: .01em;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}
.dashboard-banner-copy small {
  display: block;
  margin-top: 5px;
  color: var(--muted);
  font-size: 8px;
}
.banner-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 90px;
  color: var(--muted);
  font-size: 9px;
}

@media (max-width: 1350px) {
  .dashboard-grid { grid-template-columns: minmax(0, 1fr) 285px; }
  .quick-actions { grid-template-columns: repeat(5, minmax(100px, 1fr)); }
  .summary-metric { padding-right: 9px; padding-left: 9px; }
  .summary-metric strong { font-size: 14px; }
  .transactions-trader-row { grid-template-columns: minmax(0, 1.3fr) minmax(315px, 1fr); }
  .banner-benefits { display: none; }
  .broker-banner { grid-template-columns: 1fr 1fr; }
}

@media (max-width: 1120px) {
  .dashboard-grid { grid-template-columns: 1fr; }
  .dashboard-aside { display: grid; grid-template-columns: 1.2fr 1fr; }
  .summary-row { grid-template-columns: 1fr; }
  .balance-overview { min-height: 170px; }
}

@media (max-width: 850px) {
  .accounts-performance-row,
  .transactions-trader-row { grid-template-columns: 1fr; }
  .trading-accounts,
  .performance-overview,
  .recent-transactions,
  .trader-performance { height: auto; min-height: 238px; }
  .quick-actions { grid-template-columns: repeat(3, 1fr); }
  .dashboard-aside { grid-template-columns: 1fr; }
  .summary-metrics { grid-template-columns: repeat(2, 1fr); height: auto; }
  .summary-metric { height: 130px; border-bottom: 1px solid var(--border); }
  .summary-metric:last-child { grid-column: 1 / -1; }
}

@media (max-width: 560px) {
  .dashboard-page { padding: 8px 5px 20px !important; }
  .quick-actions { grid-template-columns: repeat(2, 1fr); }
  .account-columns { grid-template-columns: 1fr; }
  .account-stat { border-right: 0; border-bottom: 1px solid var(--border); }
  .trader-performance-body { grid-template-columns: 1fr; gap: 14px; }
  .performance-list { width: 100%; }
  .broker-banner { grid-template-columns: 1fr; }
  .banner-art { opacity: .25; }
  .banner-copy { max-width: 270px; }
  .calendar-events article { grid-template-columns: 38px 50px minmax(0, 1fr); }
  .calendar-events em { grid-column: 3; justify-self: start; }
}
</style>

<style>
.dashboard-dropdown-menu {
  margin-top: 4px;
  border: 1px solid #263a51;
  border-radius: 5px !important;
  box-shadow: 0 12px 28px rgba(0, 0, 0, .28) !important;
}

.dashboard-dropdown-list {
  min-width: 125px;
  padding: 4px !important;
  background: #101e30 !important;
}

.dashboard-dropdown-list .v-list-item {
  min-height: 30px !important;
  padding: 0 9px !important;
  border-radius: 3px;
}

.dashboard-dropdown-list .v-list-item:hover {
  background: rgba(76, 111, 255, .16) !important;
}

.dashboard-dropdown-list .v-list-item__title {
  color: #dbe5f2 !important;
  font-size: 10px !important;
}

.theme--light .dashboard-dropdown-list {
  background: #ffffff !important;
}

.theme--light .dashboard-dropdown-list .v-list-item__title {
  color: #142033 !important;
}
</style>
